import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { t } from "i18next";
import { LeafDropdown } from "../../common/leaf/LeafDropdown/LeafDropdown";
import { getCountryFlag } from "../../common/util/getCountryFlag";
import "./BusinessSelector.scss";
import { setAboName } from "../../reducers/aboName";
import { SetBootForCurrentBusiness } from "../../reducers/model/boot";
import { setBusinessSelectorData, setNavigatedByBusiness } from "../../reducers/businessSelector";
import { IconDropdownCarrot } from "../../common/icons/IconDropdownCarrot/IconDropdownCarrot";
import { WheelPicker } from "../../common/leaf/LeafWheelPicker/LeafWheelPicker";
import { Variant } from "../../common/leaf/LeafButton/LeafButton";
import { setBootLoading } from "../../reducers/boot";
import { useAppDispatch, useAppSelector } from "../../store";
import businessSelectorContent from "../../content/businessSelector";
import type { Business } from "../../services/MyBizShell/mybizShellAPI.types";
import { FetchBusinessSelectorData } from "./BusinessSelectorAPIDataLayer";
import { setRedirectTo } from "../../reducers/componentLoader";
import { getDisplayName } from "../../common/util/displayName";

type Props = {
  viewState: "desktop" | "mobile";
  callbackOnBusinessChanged?: CallableFunction;
  callbackBlockScrollWhileBusSelOpen?: CallableFunction;
};

export type Selected = {
  isoCountry: string;
  abo: string;
  aboName: string;
  aff: string;
};

export const mapBusinessSelectorData = (businesses: Business[]): Business[] => {
  return businesses.map((business) => {
    const resolvedName = getDisplayName(business.displayName, t);
    return { ...business, aboName: resolvedName, abo: `#${business.abo}` };
  });
};

export const getDisabledOptions = (businesses: Business[]) => {
  if (!businesses) return [];
  return businesses.filter((b) => b.disabledAff || b.isoCountry === "").map((b) => b.abo);
};

export const BusinessSelector = ({
  viewState,
  callbackOnBusinessChanged,
  callbackBlockScrollWhileBusSelOpen,
}: Props) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.boot);
  const aboName = useAppSelector((state) => state.aboName.name);
  const { abo: aboNum, aff, isoCountryCode: countryCode } = user;
  const { t } = useTranslation(["leafAboName", "businessSelector"]);
  const dropdownLabel = t("dropdownLabel", businessSelectorContent["dropdownLabel"], { ns: "businessSelector" });
  const { businessSelectorData } = useAppSelector((state) => state.businessSelectorData);

  let skip = false;
  if (businessSelectorData && businessSelectorData.length > 0) {
    const primaryBusinessAboNum = businessSelectorData[0].abo;
    if (primaryBusinessAboNum && primaryBusinessAboNum !== aboNum) {
      skip = true;
    }
  }

  const { errorStatus, data: busSelectorData } = FetchBusinessSelectorData(aff, aboNum, skip);
  const [dropdownData, setDropdownData] = useState<Business[]>(businessSelectorData);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [mobileSelecterKey, setMobileSelectorKey] = useState(0);

  const [selectedOption, setSelectedOption] = useState<Selected>({
    aff: aff,
    abo: aboNum,
    aboName: aboName,
    isoCountry: countryCode,
  });

  useEffect(() => {
    if (user) {
      setSelectedOption({
        aff,
        abo: `#${aboNum}`,
        aboName: aboName,
        isoCountry: countryCode,
      });
    }
  }, [user, aboName, aboNum, aff, countryCode]);

  useEffect(() => {
    if (busSelectorData) {
      const { businesses } = busSelectorData;
      const businessSelectorData = mapBusinessSelectorData(businesses);
      setDropdownData(businessSelectorData);
      dispatch(setBusinessSelectorData(businessSelectorData));
    }
  }, [busSelectorData, dispatch]);

  const displayLeafDropdown = dropdownData && !errorStatus;

  const closeMobileBusinessSelector = () => {
    if (callbackBlockScrollWhileBusSelOpen) callbackBlockScrollWhileBusSelOpen();
    setIsMobileModalOpen(false);
  };

  const toggleMobileBusinessSelector = () => {
    if (callbackBlockScrollWhileBusSelOpen) callbackBlockScrollWhileBusSelOpen();
    setIsMobileModalOpen(!isMobileModalOpen);
  };

  const handleOnMobileSelectorSave = (dropdownDataIndex: number) => {
    handleSelectedOptionChange(dropdownData[dropdownDataIndex] as Selected);
    closeMobileBusinessSelector();

    if (callbackOnBusinessChanged) {
      callbackOnBusinessChanged();
    }
  };

  useEffect(() => {
    if (dropdownData) {
      const selectedItem = dropdownData.findIndex((k) => k.abo === selectedOption.abo && k.aff === selectedOption.aff);
      setMobileSelectorKey(selectedItem);
    }
  }, [dropdownData, selectedOption, isMobileModalOpen]);

  const renderWheelPickerBusinessSelector = () => {
    if (dropdownData.length <= 1) return renderBusinessSelectorNotClickable();

    return (
      <>
        <span
          id="busSelector"
          onClick={() => toggleMobileBusinessSelector()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              toggleMobileBusinessSelector();
            }
          }}
          className="busSelector__mobile"
          data-testid="busSelector_mobile"
          role="button"
          tabIndex={0}
        >
          <h1>{aboName}</h1>
          {displayLeafDropdown && (
            <>
              <span className={"busSelector__subheading"}>
                {getCountryFlag(countryCode)}
                <span>{countryCode}</span>
                <span>#{aboNum}</span>
                <span>{<IconDropdownCarrot open={isMobileModalOpen} />}</span>
              </span>
            </>
          )}
        </span>
        <WheelPicker
          selectedOptionKey={mobileSelecterKey}
          renderTemplate={getWheelPickerRenderTemplate}
          callbackSetSelected={handleOnMobileSelectorSave}
          callbackOnClose={closeMobileBusinessSelector}
          data={dropdownData}
          disabledIndexesFromSelection={getDisabledItemIndexesForMobileSelector()}
          leafButtonEnabledVariant={Variant.BLUE}
          leafButtonDisabledVariant={Variant.DISABLED_BLUE}
          isDisplayed={isMobileModalOpen}
        />
      </>
    );
  };

  const getDisabledItemIndexesForMobileSelector = () => {
    return dropdownData.filter((business) => business.disabledAff).map((x) => dropdownData.indexOf(x));
  };

  const getWheelPickerRenderTemplate = ({
    data,
    isSelected,
    isDisabled,
  }: {
    data: Business;
    isSelected: boolean;
    isDisabled: boolean;
  }) => {
    const getStyle = () => {
      let style = "WheelPickerColumn__item WheelPickerColumn__item--leftAligned";

      if (isDisabled) {
        style = "WheelPickerColumn__item WheelPickerColumn__item--disabled WheelPickerColumn__item--leftAligned";
      } else if (isSelected) {
        style = "WheelPickerColumn__item WheelPickerColumn__item--active WheelPickerColumn__item--leftAligned";
      }
      return style;
    };

    const id = `WheelPickerColumn__item:${dropdownData.findIndex((x) => x === data)}`;

    const style = getStyle();

    return (
      <div
        key={id}
        className={`WheelPickerColumn__container ${style}`}
        id={id}
        role="option"
        aria-selected={isSelected ? "true" : "false"}
      >
        <span>{getCountryFlag(data.isoCountry)}</span>
        <span>
          <p className={style}>{data.isoCountry}</p>
        </span>
        <span>
          <p className={style}>{data.abo}</p>
        </span>
        <span>
          <p className={`${style}`}>{data.aboName}</p>
        </span>
      </div>
    );
  };

  const renderBusinessSelectorNotClickable = () => {
    return (
      <span id="busSelector" className="busSelector__mobile" data-testid="busSelector_mobile__no_click">
        <h2>{selectedOption.aboName}</h2>
        {displayLeafDropdown && (
          <>
            <span className={"busSelector__subheading--no-pointer"}>
              {getCountryFlag(countryCode)}
              <span>{countryCode}</span>
              <span>#{aboNum}</span>
            </span>
          </>
        )}
      </span>
    );
  };

  const handleSelectedOptionChange = (selectedOption: Selected) => {
    dispatch(setAboName(selectedOption.aboName));
    dispatch(setNavigatedByBusiness(true));
    const { aff, abo, isoCountry } = selectedOption;
    dispatch(setBootLoading(true));

    SetBootForCurrentBusiness({ user: { aff, abo: abo.replace(/^#/, ""), isoCountryCode: isoCountry } });
    dispatch(setRedirectTo(""));
  };

  const renderDropdownBusinessSelector = () => {
    return (
      <span id="busSelector" className="busSelector__desktop" data-testid="busSelector_desktop">
        {displayLeafDropdown && (
          <LeafDropdown
            inputData={dropdownData}
            selectedIdentifier={selectedOption}
            options={{
              disabledOptions: getDisabledOptions(dropdownData),
            }}
            functions={{
              handleSelectedOptionChange: handleSelectedOptionChange,
              getIconFunction: getCountryFlag,
            }}
            keys={{
              iconKey: "isoCountry",
              colOneKey: "isoCountry",
              colTwoKey: "abo",
              colThreeKey: "aboName",
              idKeyOne: "abo",
              idKeyTwo: "aff",
            }}
            dropdownAriaLabel={dropdownLabel}
          />
        )}
      </span>
    );
  };

  const renderViewState = () => {
    if (viewState === "desktop") return renderDropdownBusinessSelector();
    if (viewState === "mobile") return renderWheelPickerBusinessSelector();

    return renderBusinessSelectorNotClickable();
  };

  // to prevent exception throw when dropdownData is an empty array
  if (dropdownData && dropdownData.length === 0) {
    return <></>;
  }

  return renderViewState();
};
